import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Inbox, Check, MessageCircle, Wand2 } from 'lucide-react';
import { Button, Paginator } from '@jeraisonne/ui';
import { useAuth } from '@/lib/auth';
import { AvatarCircle } from '@/components/avatar-circle';
import { useFindOrCreateDiscussion } from '@/lib/discussions/queries';
import { DiscussionError } from '@/lib/discussions/types';
import {
  useTutorSubmissions,
  useSubmissionPhotoUrl,
  useMarkSubmissionStatus,
  type TutorSubmissionRow,
} from '@/lib/exercise-submissions/api';
import { usePagedList } from '@/lib/use-paged-list';

/**
 * Route tuteur `/tutor/submissions` — boîte de réception des exercices
 * envoyés par les élèves (migration 0222). Chaque soumission peut nourrir un
 * nouveau générateur paramétrique (Blueprint) via "Utiliser pour un générateur".
 */
export function TutorSubmissionsPage() {
  const { user } = useAuth();
  const submissions = useTutorSubmissions(user?.id);
  const { items: pageSubmissions, paginator } = usePagedList(submissions.data, {
    unit: 'envois',
    label: 'Pages des exercices reçus',
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 pb-[max(env(safe-area-inset-bottom),1.5rem)] sm:py-8">
      <header className="mb-5 flex items-center gap-2.5">
        <div className="bg-primary-50 flex h-10 w-10 items-center justify-center rounded-xl">
          <Inbox className="text-primary-500 h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display text-ink-900 text-xl font-extrabold leading-tight">
            Exercices reçus
          </h1>
          <p className="text-ink-500 text-sm">
            Photos envoyées par tes élèves : réponds-leur, puis classe-les.
          </p>
        </div>
      </header>

      {submissions.isLoading && <p className="text-ink-500 text-sm">Chargement…</p>}

      {submissions.data && submissions.data.length === 0 && (
        <div className="border-ink-200 text-ink-500 rounded-2xl border border-dashed bg-white p-6 text-center text-sm">
          Aucun exercice reçu pour l&apos;instant.
        </div>
      )}

      <div className="space-y-3">
        {pageSubmissions.map((submission) => (
          <SubmissionCard key={submission.id} submission={submission} />
        ))}
      </div>
      <Paginator {...paginator} />
    </div>
  );
}

function SubmissionCard({ submission }: { submission: TutorSubmissionRow }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const markStatus = useMarkSubmissionStatus();
  const findOrCreateDiscussion = useFindOrCreateDiscussion();
  const [discussionError, setDiscussionError] = useState<string | null>(null);
  const firstName = submission.student_name.split(' ')[0] || submission.student_name;

  async function handleDiscuss() {
    if (!user?.id) return;
    setDiscussionError(null);
    try {
      const discussion = await findOrCreateDiscussion.mutateAsync({
        tutorId: user.id,
        studentId: submission.student_id,
        subject: {
          scope: 'submission',
          submissionId: submission.id,
          submittedAt: submission.created_at,
        },
      });
      void navigate({ to: '/discussions/$discussionId', params: { discussionId: discussion.id } });
    } catch (err) {
      setDiscussionError(
        err instanceof DiscussionError ? err.message : "Impossible d'ouvrir la discussion.",
      );
    }
  }

  return (
    <div className="border-ink-200 space-y-3 rounded-2xl border bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2.5">
          <AvatarCircle name={submission.student_name} size="sm" />
          <div className="min-w-0">
            <p className="text-ink-900 truncate text-sm font-semibold">{submission.student_name}</p>
            <p className="text-ink-400 text-xs">
              {new Date(submission.created_at).toLocaleString('fr-FR', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>
        </div>
        {/* Classer = la soumission quitte la boîte de réception (et le compteur de l'accueil). */}
        <button
          type="button"
          onClick={() => markStatus.mutate({ id: submission.id, status: 'dismissed' })}
          disabled={markStatus.isPending}
          aria-label="Ignorer cette soumission (classer comme traitée)"
          className="text-ink-500 hover:text-ink-800 hover:bg-ink-50 flex min-h-[2.75rem] shrink-0 items-center gap-1 rounded-xl px-2 font-sans text-sm font-semibold"
        >
          <Check className="h-4 w-4" aria-hidden />
          Traité
        </button>
      </div>

      {submission.chapter_title && (
        <p className="bg-primary-50 text-primary-700 inline-block rounded-full px-2.5 py-1 text-xs font-semibold">
          {submission.chapter_title}
        </p>
      )}

      {submission.note.trim() && (
        <p className="bg-ink-50 text-ink-700 rounded-xl px-3 py-2 text-sm italic">
          « {submission.note} »
        </p>
      )}

      {submission.photos.length > 0 && (
        <div className="flex gap-2 overflow-x-auto">
          {submission.photos.map((photo) => (
            <SubmissionPhotoThumbnail key={photo.id} storagePath={photo.storage_path} />
          ))}
        </div>
      )}

      {discussionError && (
        <p role="alert" className="text-accent-700 text-xs font-medium">
          {discussionError}
        </p>
      )}

      {/* Répondre à l'élève est le geste principal ; le générateur est un usage avancé. */}
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          fullWidth
          onClick={() => void handleDiscuss()}
          loading={findOrCreateDiscussion.isPending}
          className="min-h-[2.75rem] active:scale-[0.98]"
        >
          <span className="flex items-center justify-center gap-2">
            <MessageCircle className="h-4 w-4" /> Répondre à {firstName}
          </span>
        </Button>
        <Link
          to="/tutor/blueprints/new"
          search={{ submission: submission.id }}
          onClick={() => markStatus.mutate({ id: submission.id, status: 'used' })}
          className="w-full"
        >
          <Button variant="outline" fullWidth className="min-h-[2.75rem]">
            <span className="flex items-center justify-center gap-2">
              <Wand2 className="h-4 w-4" /> En faire un générateur
            </span>
          </Button>
        </Link>
      </div>
    </div>
  );
}

function SubmissionPhotoThumbnail({ storagePath }: { storagePath: string }) {
  const { data: url } = useSubmissionPhotoUrl(storagePath);
  return (
    <div className="border-ink-200 bg-ink-50 h-32 w-32 shrink-0 overflow-hidden rounded-xl border">
      {url ? (
        // Ouvre la photo en grand (lecture de la copie).
        <a href={url} target="_blank" rel="noreferrer" aria-label="Voir la photo en grand">
          <img src={url} alt="Exercice envoyé" className="h-full w-full object-cover" />
        </a>
      ) : (
        <div className="h-full w-full animate-pulse" />
      )}
    </div>
  );
}
